import { JSX, ReactNode } from 'react';
import { I18nextProvider } from 'react-i18next';
import { LoaderProvider } from 'shared';
import i18n from '../i18n/i18n';
import { CacheProvider } from './Cache.provider';
import { DatadogProvider } from './Datadog.provider';
import { ConfirmationDialogueProvider } from './ConfirmationDialogue.provider';
import { FeatureFlagProvider } from './FeatureFlag.provider';
import { NavMenuProvider } from './NavMenu.provider';
import { PermissionsProvider } from './Permissions.provider';
import { ClientLoginProvider } from './ClientLogin.provider';
import { ClientDataProvider } from './ClientData.provider';
import { AccountCreationProvider } from './AccountCreation.provider';
import { OpenModuleProvider } from './OpenModule.provider';
import { AccountInfoProvider } from './AccountInfo.provider';
import { AccountSummaryProvider } from './AccountSummary.provider';
import { LockedFeaturesProvider } from './LockedFeatures.provider';
import { LockedFeatureDialogue } from './LockedFeatureDialogue.provider';
import { ToastContextProvider } from './Toast.provider';
import { ApprovalWorkflowProvider } from './ApprovalWorkflow.provider';
import { BrandingContextProvider } from './Branding.provider';
import { DialogProvider } from './Dialog.provider';
import { ConfirmDialogProvider } from './ConfirmDialog.provider';
import { UserSettingsProvider } from './UserSettings.provider';
import SharedServicesProvider from './SharedServicesProvider';
import { LoggingProvider } from './Logging.provider';
import { SessionProvider } from './Session.provider';
import { LogTimestampProvider } from './LogTimestamp.provider';
import { HomepageProvider } from './Homepage';
import { MSEntraProvider } from './MsEntra.provider';

export const MainProvider = ({ children }: { children: ReactNode }): JSX.Element => (
  /* Shared's fullscreen blocking loader. Outermost: any provider below may call
  useBlockingLoader (via useDataFetchIndicator), which throws without it. */
  <LoaderProvider>
    <MSEntraProvider>
      <CacheProvider>
        <SessionProvider>
          <LogTimestampProvider>
            <DatadogProvider>
              <I18nextProvider i18n={i18n}>
                <PermissionsProvider>
                  <ClientDataProvider>
                    <ApprovalWorkflowProvider>
                      <FeatureFlagProvider>
                        <LockedFeaturesProvider>
                          <LockedFeatureDialogue>
                            <ConfirmationDialogueProvider>
                              <NavMenuProvider>
                                <AccountSummaryProvider>
                                  <AccountInfoProvider>
                                    <ClientLoginProvider>
                                      <BrandingContextProvider>
                                        <AccountCreationProvider>
                                          <ToastContextProvider>
                                            <ConfirmDialogProvider>
                                              <DialogProvider>
                                                <OpenModuleProvider>
                                                  <LoggingProvider>
                                                    <UserSettingsProvider>
                                                      <HomepageProvider>
                                                        {/* Keep SharedServicesProvider innermost: it renders shared's
                                                        dialog/toast/alert content as siblings of children, which must see every
                                                        host context. A shared hook inside a host-opened Modal is unsupported.
                                                        It also mounts the alert-dialog provider, which is why only toast and
                                                        dialog are mounted above. */}
                                                        <SharedServicesProvider>{children}</SharedServicesProvider>
                                                      </HomepageProvider>
                                                    </UserSettingsProvider>
                                                  </LoggingProvider>
                                                </OpenModuleProvider>
                                              </DialogProvider>
                                            </ConfirmDialogProvider>
                                          </ToastContextProvider>
                                        </AccountCreationProvider>
                                      </BrandingContextProvider>
                                    </ClientLoginProvider>
                                  </AccountInfoProvider>
                                </AccountSummaryProvider>
                              </NavMenuProvider>
                            </ConfirmationDialogueProvider>
                          </LockedFeatureDialogue>
                        </LockedFeaturesProvider>
                      </FeatureFlagProvider>
                    </ApprovalWorkflowProvider>
                  </ClientDataProvider>
                </PermissionsProvider>
              </I18nextProvider>
            </DatadogProvider>
          </LogTimestampProvider>
        </SessionProvider>
      </CacheProvider>
    </MSEntraProvider>
  </LoaderProvider>
);
